/**
 * Department Allocation Service
 * 
 * This service layer provides an abstraction over department allocation-related API calls.
 * It handles fetching departments, users, and providers for facility department allocation.
 * 
 * @module DepartmentAllocationService
 */

import { getDepartmentsByFacilityId, getStaff, getProviders, updateDepartment, getDepartmentById } from '../utils/apiClient';

/**
 * Fetches all departments for a specific facility
 * 
 * @param {string} facilityId - ID of the facility
 * @returns {Promise<Object>} Response with departments array
 * 
 * @example
 * const result = await DepartmentAllocationService.getDepartmentsByFacility('facility-id');
 */
export const getDepartmentsByFacility = async (facilityId) => {
  try {
    console.log('🏥 Fetching departments for facility:', facilityId);
    const response = await getDepartmentsByFacilityId(facilityId);
    
    console.log('🔍 Raw departments API response:', response);
    
    // Handle the API response structure
    if (response && (response.statusCode === 'SUCCESS' || response.success)) {
      const departments = response.response || response.data || [];
      console.log('✅ Successfully fetched departments:', departments);
      
      return {
        success: true,
        data: departments,
        message: 'Departments fetched successfully'
      };
    }
    
    return {
      success: false,
      data: [],
      message: response.message || 'Failed to fetch departments'
    };
  } catch (error) {
    console.error('❌ Error in getDepartmentsByFacility:', error);
    return {
      success: false,
      data: [],
      message: 'Failed to fetch departments. Please try again.'
    };
  }
};

/**
 * Fetches all staff/users for a specific facility
 * 
 * @param {string} facilityId - ID of the facility
 * @returns {Promise<Object>} Response with users array
 * 
 * @example
 * const result = await DepartmentAllocationService.getUsersByFacility('facility-id');
 */
export const getUsersByFacility = async (facilityId) => {
  try {
    console.log('👥 Fetching users for facility:', facilityId);
    
    const params = { facilityId, isActive: true };
    
    const response = await getStaff(params);
    
    console.log('🔍 Raw users API response:', response);
    
    // Handle the API response structure
    if (response && (response.statusCode === 'SUCCESS' || response.success || Array.isArray(response))) {
      const users = response.response || response.data || response || [];
      console.log('✅ Successfully fetched users:', users);
      
      return {
        success: true,
        data: users,
        message: 'Users fetched successfully'
      };
    }
    
    return {
      success: false,
      data: [],
      message: response.message || 'Failed to fetch users'
    };
  } catch (error) {
    console.error('❌ Error in getUsersByFacility:', error);
    return {
      success: false,
      data: [],
      message: 'Failed to fetch users. Please try again.'
    };
  }
};

/**
 * Fetches all providers for a specific facility
 * 
 * @param {string} facilityId - ID of the facility
 * @returns {Promise<Object>} Response with providers array
 * 
 * @example
 * const result = await DepartmentAllocationService.getProvidersByFacility('facility-id');
 */
export const getProvidersByFacility = async (facilityId) => {
  try {
    console.log('🩺 Fetching providers for facility:', facilityId);
    
    const params = { facility_id:facilityId };
    
    const response = await getProviders(params);
    
    console.log('🔍 Raw providers API response:', response);
    
    // Handle the API response structure
    // Note: getProviders already returns response.data.response (just the providers array)
    if (response && Array.isArray(response)) {
      console.log('✅ Successfully fetched providers:', response);
      console.log('📋 Provider sample (showing name field):', {
        fullName: response[0]?.fullName,
        npi: response[0]?.npi,
        specialties: response[0]?.specialties
      });
      
      return {
        success: true,
        data: response,
        message: 'Providers fetched successfully'
      };
    }
    
    console.log('❌ Invalid provider response format (expected array):', response);
    return {
      success: false,
      data: [],
      message: 'Failed to fetch providers - invalid response format'
    };
  } catch (error) {
    console.error('❌ Error in getProvidersByFacility:', error);
    return {
      success: false,
      data: [],
      message: 'Failed to fetch providers. Please try again.'
    };
  }
};

/**
 * Fetches all data needed for department allocation
 * 
 * @param {string} facilityId - ID of the facility
 * @param {string} departmentId - ID of the department (optional, used only for URL state)
 * @returns {Promise<Object>} Response with all allocation data
 * 
 * @example
 * const result = await DepartmentAllocationService.getAllAllocationData('facility-id');
 */
export const getAllAllocationData = async (facilityId, departmentId = null) => {
  try {
    console.log('📊 Fetching all allocation data for facility:', facilityId);
    
    // Fetch departments first
    const departmentsResult = await getDepartmentsByFacility(facilityId);
    
    // Use the first department if no specific department is selected (for URL state only)
    const targetDepartmentId = departmentId || departmentsResult.data?.[0]?._id;
    
    // Fetch all users and providers for the facility (not filtered by department)
    const [usersResult, providersResult] = await Promise.all([
      getUsersByFacility(facilityId),
      getProvidersByFacility(facilityId)
    ]);
    
    console.log('📈 All allocation data fetched:', {
      departments: departmentsResult.data?.length || 0,
      users: usersResult.data?.length || 0,
      providers: providersResult.data?.length || 0
    });
    
    return {
      success: true,
      data: {
        departments: departmentsResult.data || [],
        users: usersResult.data || [],
        providers: providersResult.data || [],
        selectedDepartmentId: targetDepartmentId
      },
      message: 'All allocation data fetched successfully'
    };
  } catch (error) {
    console.error('❌ Error in getAllAllocationData:', error);
    return {
      success: false,
      data: {
        departments: [],
        users: [],
        providers: [],
        selectedDepartmentId: null
      },
      message: 'Failed to fetch allocation data. Please try again.'
    };
  }
};

/**
 * Saves department allocations
 * 
 * @param {string} facilityId - ID of the facility
 * @param {Object} allocationData - Allocation data to save
 * @returns {Promise<Object>} Save response
 * 
 * @example
 * const result = await DepartmentAllocationService.saveAllocations('facility-id', allocationData);
 */
/**
 * Saves staff allocation for a department
 * 
 * @param {string} departmentId - ID of the department to update
 * @param {string[]} staffIds - Array of staff IDs to assign to the department
 * @returns {Promise<Object>} Response indicating success or failure
 * 
 * @example
 * const result = await DepartmentAllocationService.saveStaffAllocation('dept-id', ['staff1', 'staff2']);
 */
export const saveStaffAllocation = async (departmentId, staffIds) => {
  try {
    console.log('👥 Saving staff allocation for department:', departmentId, { staffIds });
    
    const updateData = {
      staffIds: staffIds
    };
    
    const response = await updateDepartment(departmentId, updateData);
    
    console.log('✅ Staff allocation saved successfully:', response);
    
    return {
      success: true,
      message: 'Staff allocation saved successfully',
      data: response
    };
  } catch (error) {
    console.error('❌ Error in saveStaffAllocation:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to save staff allocation. Please try again.'
    };
  }
};

/**
 * Fetches department details by ID including assigned staff and providers
 * Uses includeStaff=true and includeRenderingProviders=true parameters
 * 
 * Response includes staff/renderingProviders arrays with full objects.
 * This function extracts active staff/provider IDs for easy consumption.
 * 
 * @param {string} departmentId - ID of the department
 * @returns {Promise<Object>} Response with department details, plus extracted staffIds and renderingProviderIds arrays
 * 
 * @example
 * const result = await DepartmentAllocationService.getDepartmentDetails('dept-id');
 * // API call: GET /api/v1/departments/{id}?includeStaff=true&includeRenderingProviders=true
 * // result.data.staffIds = ['id1', 'id2'] (only active staff)
 * // result.data.renderingProviderIds = ['id1'] (only active providers)
 */
export const getDepartmentDetails = async (departmentId) => {
  try {
    console.log('🏢 Fetching department details for:', departmentId);
    console.log('📋 With parameters:', { includeStaff: true, includeRenderingProviders: true });
    
    // Add query parameters to include staff and rendering providers
    const params = {
      includeStaff: true,
      includeRenderingProviders: true
    };
    
    const response = await getDepartmentById(departmentId, params);
    
    console.log('🔍 Raw department details response:', response);
    
    // Handle the API response structure
    if (response && (response.statusCode === 'SUCCESS' || response.success)) {
      const department = response.response || response.data || response;
      
      console.log('✅ Successfully fetched department details:', department);
      
      // Extract staff IDs from staff array (only active staff)
      const staffIds = department.staff 
        ? department.staff
            .filter(staff => staff.is_active === true)
            .map(staff => staff.id)
        : [];
      
      // Extract provider IDs from renderingProviders array (only active providers)
      const renderingProviderIds = department.renderingProviders
        ? department.renderingProviders
            .filter(provider => provider.is_active === true)
            .map(provider => provider.id)
        : [];
      
      console.log('👥 Active staff IDs extracted:', staffIds);
      console.log('🏥 Active provider IDs extracted:', renderingProviderIds);
      
      // Return enhanced department data with extracted IDs
      const enhancedDepartment = {
        ...department,
        staffIds,
        renderingProviderIds
      };
      
      return {
        success: true,
        data: enhancedDepartment,
        message: 'Department details retrieved successfully'
      };
    }
    
    console.warn('⚠️ Unexpected response format from getDepartmentById:', response);
    return {
      success: false,
      data: null,
      message: 'Unexpected response format'
    };
  } catch (error) {
    console.error('❌ Error in getDepartmentDetails:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || 'Failed to fetch department details'
    };
  }
};

/**
 * Saves provider allocation for a department
 * 
 * @param {string} departmentId - ID of the department to update
 * @param {Object[]} renderingProviderIds - Array of provider objects with id and role
 * @param {string} renderingProviderIds[].id - Provider ID
 * @param {string[]} renderingProviderIds[].role - Array of roles for the provider
 * @returns {Promise<Object>} Response indicating success or failure
 * 
 * @example
 * const result = await DepartmentAllocationService.saveProviderAllocation('dept-id', [
 *   { id: 'provider1', role: ['General Surgeon'] },
 *   { id: 'provider2', role: ['Cardiologist'] }
 * ]);
 */
export const saveProviderAllocation = async (departmentId, renderingProviderIds) => {
  try {
    console.log('🏥 Saving provider allocation for department:', departmentId, { renderingProviderIds });
    
    const updateData = {
      renderingProviderIds: renderingProviderIds
    };
    
    const response = await updateDepartment(departmentId, updateData);
    
    console.log('✅ Provider allocation saved successfully:', response);
    
    return {
      success: true,
      message: 'Provider allocation saved successfully',
      data: response
    };
  } catch (error) {
    console.error('❌ Error in saveProviderAllocation:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to save provider allocation. Please try again.'
    };
  }
};

// Export as default object for easier importing
const DepartmentAllocationService = {
  getDepartmentsByFacility,
  getUsersByFacility,
  getProvidersByFacility,
  getAllAllocationData,
  getDepartmentDetails,
  saveStaffAllocation,
  saveProviderAllocation
};

export default DepartmentAllocationService;
